/**
 * Marca do integrador e modo de exibição na tela — Nexora Grid (frontend, US-4502, US-4507).
 *
 * Criado em: 02/11/2026 · Última modificação: 02/11/2026 · Versão: 1.0.0
 *
 * `MarcaProvider` lê `GET /api/v1/marca` (público, antes do login) e aplica: as cores da marca já ajustadas por modo
 * (variáveis `--marca-*` do `design/tokens.css`), o título da aba, o ícone e a cor do navegador. A última marca
 * fica no `localStorage` só para a tela não piscar com a padrão na próxima abertura.
 *
 * Modo de exibição (D-4505: escolha do usuário): `escuro` (padrão), `claro` ou `contraste`, em `data-modo` na raiz.
 * Vem de `usuario.modo_exibicao`; o endereço (`?modo=`, telas do videowall) tem precedência; trocar grava no
 * perfil (`PUT /api/v1/auth/preferencias`) e vale em qualquer computador.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "./api";
import { MARCA_PADRAO } from "./marcaPadrao";
import { useSessao } from "./sessao";

export type ModoExibicao = "escuro" | "claro" | "contraste";
export const MODOS: { valor: ModoExibicao; texto: string }[] = [
  { valor: "escuro", texto: "Escuro" }, { valor: "claro", texto: "Claro" }, { valor: "contraste", texto: "Alto contraste" }];
type Cores = { primaria: string; secundaria: string; destaque: string };
export type ArquivoMarca = { url: string; tipo: string };
export type Marca = {
  produto: string; nome_curto: string; empresa: string; razao_social: string; cnpj: string; site: string;
  email_suporte: string; telefone_suporte: string; remetente_email: string;
  cores: Cores; cores_por_modo: Record<ModoExibicao, Cores>;
  arquivos: { colorida: ArquivoMarca; negativa: ArquivoMarca; icone: ArquivoMarca };
  icones: { "32": string; "192": string; "512": string };
  versao: number;
};

const CHAVE = "nexora.marca";
let marcaGlobal: Marca = MARCA_PADRAO;
/** Marca atual para módulos sem React (rótulos em `permissoes.ts`, `admin/tipos.ts`). */
export function marcaAtual(): Marca { return marcaGlobal; }
/** O sufixo do produto ("Grid"): o que vem depois do nome curto, ao lado da logomarca. */
export function sufixoProduto(m: Marca): string {
  const resto = m.produto.startsWith(m.nome_curto) ? m.produto.slice(m.nome_curto.length) : "";
  return resto.trim();
}
const CHAVE_MODO = "nexora.modo";

type Contexto = {
  marca: Marca;
  /** Logomarca para o modo: a colorida no claro, a negativa no escuro e no alto contraste. */
  logo: (modo?: ModoExibicao) => string;
  /** "equipe <empresa>" — como o usuário chama a equipe do integrador. */
  equipe: string;
  modo: ModoExibicao;
  trocarModo: (modo: ModoExibicao) => Promise<void>;
  recarregar: () => Promise<void>;
};
const ContextoMarca = createContext<Contexto | null>(null);

function lerGuardada(): Marca | null {
  try {
    const m = JSON.parse(localStorage.getItem(CHAVE) ?? "null") as Marca | null;
    return m && typeof m.produto === "string" && m.cores_por_modo ? m : null;
  } catch { return null; }
}

/** Aplica as cores da marca nas variáveis do design system (uma por modo; o tokens.css escolhe pelo `data-modo`). */
export function aplicarCores(marca: Marca, raiz: HTMLElement = document.documentElement): void {
  for (const modo of ["escuro", "claro", "contraste"] as const) {
    const c = marca.cores_por_modo[modo];
    if (!c) continue;
    raiz.style.setProperty(`--marca-primaria-${modo}`, c.primaria);
    raiz.style.setProperty(`--marca-secundaria-${modo}`, c.secundaria);
    raiz.style.setProperty(`--marca-destaque-${modo}`, c.destaque);
  }
}

function aplicarCabecalho(marca: Marca): void {
  document.title = marca.produto;
  const definir = (seletor: string, criar: () => HTMLElement, atributo: string, valor: string) => {
    let el = document.head.querySelector(seletor) as HTMLElement | null;
    if (!el) { el = criar(); document.head.appendChild(el); }
    el.setAttribute(atributo, valor);
  };
  const link = (rel: string) => () => Object.assign(document.createElement("link"), { rel });
  definir('link[rel="icon"]', link("icon"), "href", marca.icones["32"]);
  document.head.querySelector('link[rel="icon"]')?.removeAttribute("sizes");
  document.head.querySelector('link[rel="icon"]')?.setAttribute("type", marca.icones["32"].endsWith(".png") || marca.arquivos.icone.tipo === "image/png" ? "image/png" : "image/svg+xml");
  definir('link[rel="apple-touch-icon"]', link("apple-touch-icon"), "href", marca.icones["192"]);
  definir('meta[name="theme-color"]', () => Object.assign(document.createElement("meta"), { name: "theme-color" }), "content", marca.cores.primaria);
}

function modoValido(v: unknown): v is ModoExibicao {
  return v === "escuro" || v === "claro" || v === "contraste";
}

export function MarcaProvider({ children }: { children: ReactNode }) {
  const { usuario, atualizarUsuario } = useSessao();
  const [marca, setMarca] = useState<Marca>(() => lerGuardada() ?? MARCA_PADRAO);
  const [modoLocal, setModoLocal] = useState<ModoExibicao | null>(() => {
    try { const m = localStorage.getItem(CHAVE_MODO); return modoValido(m) ? m : null; } catch { return null; }
  });
  const doEndereco = new URLSearchParams(window.location.search).get("modo");
  const modo: ModoExibicao = modoValido(doEndereco) ? doEndereco
    : modoValido(usuario?.modo_exibicao) ? usuario!.modo_exibicao as ModoExibicao : modoLocal ?? "escuro";

  const recarregar = useCallback(async () => {
    try {
      const resposta = await fetch("/api/v1/marca", { headers: { Accept: "application/json" } });
      if (!resposta.ok) return;
      const nova = (await resposta.json()) as Marca;
      setMarca(nova);
      try { localStorage.setItem(CHAVE, JSON.stringify(nova)); } catch { /* só nesta sessão */ }
    } catch { /* sem backend: fica a guardada ou a padrão */ }
  }, []);
  useEffect(() => { void recarregar(); }, [recarregar]);
  marcaGlobal = marca;
  useEffect(() => { aplicarCores(marca); aplicarCabecalho(marca); }, [marca]);
  useEffect(() => { document.documentElement.dataset.modo = modo; }, [modo]);

  const trocarModo = useCallback(async (novo: ModoExibicao) => {
    setModoLocal(novo);
    try { localStorage.setItem(CHAVE_MODO, novo); } catch { /* só nesta sessão */ }
    if (!usuario) return;
    atualizarUsuario({ ...usuario, modo_exibicao: novo });
    await api("/v1/auth/preferencias", { metodo: "PUT", corpo: { modo_exibicao: novo } });
  }, [usuario, atualizarUsuario]);

  const valor = useMemo<Contexto>(() => ({
    marca, modo, trocarModo, recarregar, equipe: `equipe ${marca.empresa}`,
    logo: (m = modo) => (m === "claro" ? marca.arquivos.colorida.url : marca.arquivos.negativa.url),
  }), [marca, modo, trocarModo, recarregar]);
  return <ContextoMarca.Provider value={valor}>{children}</ContextoMarca.Provider>;
}

export function useMarca(): Contexto {
  const c = useContext(ContextoMarca);
  if (!c) throw new Error("useMarca fora do MarcaProvider");
  return c;
}

/** Logomarca com o nome curto como texto alternativo (login, topo, confirmações). */
export function Logomarca({ className, modo }: { className?: string; modo?: ModoExibicao }) {
  const { marca, logo } = useMarca();
  return <img src={logo(modo)} alt={marca.nome_curto} className={className} />;
}

/** Título das telas de entrada: logomarca e o resto do nome do produto ("GRID"). */
export function TituloMarca() {
  const { marca } = useMarca();
  const sufixo = sufixoProduto(marca);
  return (
    <h1 className="nx-titulo marca-login"><Logomarca className="logo-login" />
      {sufixo && <span className="gradiente">{sufixo.toUpperCase()}</span>}</h1>
  );
}
